import { EditorState, NodeSelection, TextSelection } from "prosemirror-state";
import { Node } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal as createReactPortal } from "react-dom";
import { useDebouncedFn } from "beautiful-react-hooks";
import "prosemirror-menu/style/menu.css";
import "prosemirror-view/style/prosemirror.css";
import TooltipMenu from "../../editor/TooltipMenu";
import {
  BLUR_SELECTION_STYLES,
  createBlurSelectionPlugin,
} from "../../editor/blurSelectionPlugin";
import ActiveParagraphPlaceholderPlugin from "../../editor/ActiveParagraphPlaceholderPlugin";
import { FormLanguageContext } from "../../formElements/FormElement";
import { exampleSetup } from "prosemirror-example-setup";
import ReportTitlePlaceholderPlugin from "../../editor/ReportTitlePlaceholderPlugin";
import {
  reportBodySchema,
  setCollapsibleBlocksClosed,
  migrateInlineImagesToBlock,
} from "../widgets/prosemirror/reportBodySchema";
import { createImageDropPlugin } from "../widgets/prosemirror/imageDropPlugin";
import { createFriendlyInlineAtomPlugin } from "../widgets/prosemirror/friendlyAtomPlugin";
import { createReportImageUploader } from "../utils/uploadReportImage";
import { useApolloClient } from "@apollo/client";
import { useReactNodeViewPortals } from "../ReactNodeView/PortalProvider";
import { createReactNodeView } from "../ReactNodeView";
import { buildReportCommandGroups } from "../widgets/widgets";
import { ReportWidgetNodeView } from "../widgets/ReportWidgetNodeView";
import { TimeSeriesProcessingModal } from "../widgets/TimeSeriesProcessingModal";
import { DetailsView } from "../widgets/prosemirror/details";
import {
  TabContainerView,
  createProtectTabContainerPlugin,
} from "../widgets/prosemirror/tabs";
import {
  CompatibleSpatialMetricDetailsFragment,
  OverlaySourceDetailsFragment,
  ProjectReportingLayersDocument,
  useProjectReportingLayersQuery,
  usePreprocessSourceMutation,
  useCopyDataLibraryTemplateMutation,
  BaseDraftReportContextDocument,
  useUpdateReportCardBodyMutation,
  useDraftReportDependenciesQuery,
  useDraftReportOverlaySourcesQuery,
  SourceProcessingJobDetailsFragment,
  useDeleteReportCardMutation,
  ReportDependenciesDocument,
  ReportOverlaySourcesDocument,
  SpatialMetricState,
} from "../../generated/graphql";
import { useTranslation } from "react-i18next";
import { useSlashCommandPalette } from "../hooks/useSlashCommandPalette";
import getSlug from "../../getSlug";
import { LayerPickerList } from "../widgets/LayerPickerDropdown";
import { useHistory } from "react-router-dom";
import { ProsemirrorBodyJSON } from "../cards/cards";
import { useGlobalErrorHandler } from "../../components/GlobalErrorHandler";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import {
  extractMetricDependenciesFromReportBody,
  hashMetricDependency,
  MetricDependency,
} from "overlay-engine";
import {
  buildOverlayStableIdToSourceUrlMap,
  EMPTY_FRAGMENT_SUBJECT_CATALOG,
  EMPTY_HYDRATED_METRICS_DETAILS,
  EMPTY_OVERLAY_SOURCES,
  EMPTY_SLIM_METRICS,
  hydrateSpatialMetrics,
} from "../utils/hydrateSpatialMetrics";
import ReportCardLoadingIndicator from "./ReportCardLoadingIndicator";
import { ReportUIStateContext } from "../context/ReportUIStateContext";
import { useBaseReportContext } from "../context/BaseReportContext";
import { useSubjectReportContext } from "../context/SubjectReportContext";
import { usePrimaryGeography } from "../hooks/usePrimaryGeography";
import type { SketchClassPrimaryGeoFields } from "../hooks/usePrimaryGeography";
import { CalculationDetailsModal } from "./CalculationDetailsModal";
import { SketchGeometryType } from "../../generated/graphql";
import useIsSuperuser from "../../useIsSuperuser";

interface ReportCardBodyEditorProps {
  /**
   * The Prosemirror document to edit
   */
  body: any;
  /**
   * Whether this is an input field (affects editor configuration)
   */
  isInput?: boolean;
  /**
   * Optional className for styling
   */
  className?: string;
  /**
   *
   */
  metrics: CompatibleSpatialMetricDetailsFragment[];
  sources: OverlaySourceDetailsFragment[];
  cardId: number;
  preselectTitle?: boolean;
  footerContainerRef: React.RefObject<HTMLDivElement>;
}

function cloneBodyForSave(body: ProsemirrorBodyJSON): ProsemirrorBodyJSON {
  return JSON.parse(JSON.stringify(body));
}

function normalizedBodyForSave(body: ProsemirrorBodyJSON): ProsemirrorBodyJSON {
  return setCollapsibleBlocksClosed(cloneBodyForSave(body));
}

function bodiesAreEqual(
  a: ProsemirrorBodyJSON,
  b: ProsemirrorBodyJSON
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Skip URL substitution so fingerprints compare dependency shape only (stable ids etc.). */
const EMPTY_OVERLAY_SOURCE_URLS: { [stableId: string]: string } = {};

function metricDependenciesFingerprint(body: ProsemirrorBodyJSON): string {
  const dependencies: MetricDependency[] = [];
  extractMetricDependenciesFromReportBody(body, dependencies);
  const hashes = dependencies.map((d) =>
    hashMetricDependency(d, EMPTY_OVERLAY_SOURCE_URLS)
  );
  hashes.sort();
  return hashes.join("\n");
}

function ReportCardBodyEditorInner({
  body,
  isInput = false,
  className = "",
  metrics,
  sources,
  cardId,
  preselectTitle = false,
  footerContainerRef,
}: ReportCardBodyEditorProps) {
  const {
    editing,
    setEditing,
    setShowCalcDetails,
    showCalcDetails,
    onEditorReadyForFocus,
    pendingWidgetSettings,
    clearPendingWidgetSettings,
  } = useContext(ReportUIStateContext);

  const onEditorReadyForFocusRef = useRef(onEditorReadyForFocus);
  onEditorReadyForFocusRef.current = onEditorReadyForFocus;
  const { geographies, report } = useBaseReportContext();
  const subjectReportContext = useSubjectReportContext();
  const sketch = subjectReportContext.data?.sketch;
  const sketchClass = sketch?.sketchClass;
  const sketchClassForPrimaryGeography: SketchClassPrimaryGeoFields =
    sketchClass ?? {
      geometryType: SketchGeometryType.Polygon,
      clippingGeographies: [],
      validChildren: [],
      project: { sketchClasses: [] },
    };
  const { clippingGeography: primaryClippingGeography } = usePrimaryGeography(
    sketchClassForPrimaryGeography,
    geographies
  );

  const refetchDraftReportTree = useMemo(
    () => [
      // {
      //   query: DraftReportDocument,
      //   variables: { sketchClassId: sketchClass.id },
      // },
      {
        query: BaseDraftReportContextDocument,
        variables: { reportId: report.id },
      },
    ],
    [report.id]
  );

  const [reportBodyHasChanges, setReportBodyHasChanges] = useState(false);
  const [draftBody, setDraftBody] = useState<ProsemirrorBodyJSON>(
    JSON.parse(JSON.stringify(body))
  );
  const onError = useGlobalErrorHandler();
  const apolloClient = useApolloClient();
  const projectId = sketchClass?.projectId;

  const uploadFile = useMemo(
    () => createReportImageUploader(apolloClient, projectId || 0, onError),
    [apolloClient, projectId, onError]
  );

  const sourceUrlMap = useMemo(() => {
    return sources.reduce((acc, s) => {
      if (s.tableOfContentsItemId && s.sourceUrl) {
        acc[s.tableOfContentsItemId] = s.sourceUrl;
      }
      return acc;
    }, {} as { [tableOfContentsItemId: number]: string });
  }, [sources]);

  const [updateReportCard, updateReportCardState] =
    useUpdateReportCardBodyMutation({
      onError,
    });

  const saveWithBody = useCallback(
    async (nextBody: ProsemirrorBodyJSON) => {
      const savedBody = normalizedBodyForSave(nextBody);
      const currentSavedBody = normalizedBodyForSave(body);
      if (bodiesAreEqual(savedBody, currentSavedBody)) {
        setEditing(null);
        return;
      }
      const dependenciesChanged =
        metricDependenciesFingerprint(savedBody) !==
        metricDependenciesFingerprint(currentSavedBody);
      await updateReportCard({
        variables: {
          id: cardId,
          body: savedBody,
        },
        refetchQueries: dependenciesChanged
          ? [ReportDependenciesDocument, ReportOverlaySourcesDocument]
          : undefined,
        awaitRefetchQueries: dependenciesChanged,
      });
      setEditing(null);
    },
    [updateReportCard, body, cardId, setEditing]
  );

  const handleCardSave = useCallback(
    () => saveWithBody(draftBody),
    [saveWithBody, draftBody]
  );

  const history = useHistory();
  const { t } = useTranslation("sketching");
  const { t: tAdmin } = useTranslation("admin:reports");

  const [additionalDependencies, setAdditionalDependencies] = useState<
    MetricDependency[]
  >([]);

  const draftDependenciesQuery = useDraftReportDependenciesQuery({
    variables: {
      input: {
        nodeDependencies: additionalDependencies.map((d) => ({
          ...d,
          hash: hashMetricDependency(d, sourceUrlMap),
        })),
        sketchId: sketch?.id,
      },
    },
    skip: additionalDependencies.length === 0 || !editing || !sketch?.id,
    onError,
    fetchPolicy: "cache-and-network",
  });

  const draftOverlaySourcesQuery = useDraftReportOverlaySourcesQuery({
    variables: {
      input: {
        nodeDependencies: additionalDependencies.map((d) => ({
          ...d,
          hash: hashMetricDependency(d, sourceUrlMap),
        })),
        sketchId: sketch?.id,
      },
    },
    skip: additionalDependencies.length === 0 || !editing || !sketch?.id,
    onError,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    const draftDeps = draftDependenciesQuery.data?.draftReportDependencies;
    const slim = draftDeps?.metrics ?? EMPTY_SLIM_METRICS;
    const anySpatialMetricPending = slim.some(
      (m) =>
        m.state !== SpatialMetricState.Complete &&
        m.state !== SpatialMetricState.Error
    );
    if (
      !draftDependenciesQuery.loading &&
      anySpatialMetricPending &&
      !(additionalDependencies.length === 0 || !editing)
    ) {
      const ref = setInterval(() => {
        draftDependenciesQuery.refetch();
      }, 1000);
      return () => clearInterval(ref);
    }
  }, [
    draftDependenciesQuery.loading,
    draftDependenciesQuery.data?.draftReportDependencies?.metrics,
    draftDependenciesQuery,
    additionalDependencies,
    editing,
  ]);

  useEffect(() => {
    if (
      !draftOverlaySourcesQuery.loading &&
      !draftOverlaySourcesQuery.data?.draftReportOverlaySources?.ready &&
      !(additionalDependencies.length === 0 || !editing)
    ) {
      const ref = setInterval(() => {
        draftOverlaySourcesQuery.refetch();
      }, 1000);
      return () => clearInterval(ref);
    }
  }, [
    draftOverlaySourcesQuery.loading,
    draftOverlaySourcesQuery.data?.draftReportOverlaySources?.ready,
    draftOverlaySourcesQuery,
    additionalDependencies,
    editing,
  ]);

  const draftOverlaySources =
    draftOverlaySourcesQuery.data?.draftReportOverlaySources?.overlaySources ??
    EMPTY_OVERLAY_SOURCES;

  const draftDependencyListForHydration = useMemo(
    () => [
      ...extractMetricDependenciesFromReportBody(draftBody),
      ...additionalDependencies,
    ],
    [draftBody, additionalDependencies]
  );

  const hydratedDraftMetrics = useMemo(() => {
    const draftDeps = draftDependenciesQuery.data?.draftReportDependencies;
    const slim = draftDeps?.metrics ?? EMPTY_SLIM_METRICS;
    if (slim.length === 0) {
      return EMPTY_HYDRATED_METRICS_DETAILS;
    }
    const urlMap = buildOverlayStableIdToSourceUrlMap(draftOverlaySources);
    return hydrateSpatialMetrics({
      slimMetrics: slim,
      dependencies: draftDependencyListForHydration,
      overlaySourceUrlByStableId: urlMap,
      fragmentSubjectCatalog:
        draftDeps?.fragmentSubjectCatalog ?? EMPTY_FRAGMENT_SUBJECT_CATALOG,
    });
  }, [
    draftDependenciesQuery.data?.draftReportDependencies?.metrics,
    draftDependenciesQuery.data?.draftReportDependencies
      ?.fragmentSubjectCatalog,
    draftDependencyListForHydration,
    draftOverlaySources,
  ]);

  const draftDependencyFailuresByHash = useMemo(() => {
    const errs =
      draftDependenciesQuery.data?.draftReportDependencies
        ?.dependencyResolutionErrors;
    if (!errs?.length) {
      return {} as { [dependencyHash: string]: string };
    }
    return Object.fromEntries(
      errs.map((e) => [e.dependencyHash, e.message] as const)
    );
  }, [
    draftDependenciesQuery.data?.draftReportDependencies
      ?.dependencyResolutionErrors,
  ]);

  const allDependencies = useMemo(() => {
    const allMetrics = [...metrics] as CompatibleSpatialMetricDetailsFragment[];
    const allSourceProcessingJobs = [
      ...sources
        .filter((s) => s.sourceProcessingJob)
        .map((s) => s.sourceProcessingJob),
    ] as SourceProcessingJobDetailsFragment[];

    for (const source of draftOverlaySources) {
      if (
        source.sourceProcessingJob &&
        !allSourceProcessingJobs.find(
          (j) => j.jobKey === source.sourceProcessingJob?.jobKey
        )
      ) {
        allSourceProcessingJobs.push(source.sourceProcessingJob);
      }
    }
    for (const metric of hydratedDraftMetrics) {
      if (metric.id && !allMetrics.find((m) => m.id === metric.id)) {
        allMetrics.push(metric);
      }
    }
    return {
      metrics: allMetrics,
      sourceProcessingJobs: allSourceProcessingJobs,
    };
  }, [metrics, sources, draftOverlaySources, hydratedDraftMetrics]);

  // Handle navigation blocking when editing
  useEffect(() => {
    if (!reportBodyHasChanges) return;
    const message = t(
      "You have unsaved changes to a report card. Are you sure you want to leave?"
    );

    // Handle browser refresh/close (beforeunload)
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    // Handle React Router navigation
    const unblock = history.block(() => message);

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();
    };
  }, [history, t, reportBodyHasChanges]);

  const { schema, plugins } = useMemo(() => {
    const schema = reportBodySchema;
    const plugins = [
      ...exampleSetup({ schema, menuBar: false }),
      ReportTitlePlaceholderPlugin(),
      createBlurSelectionPlugin(),
      ActiveParagraphPlaceholderPlugin(),
      createImageDropPlugin(schema, uploadFile),
      createFriendlyInlineAtomPlugin(),
      createProtectTabContainerPlugin(),
    ];
    return { schema, plugins };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const langContext = useContext(FormLanguageContext);
  const currentLangCode = langContext?.lang?.code || "EN";

  const reportingLayersQuery = useProjectReportingLayersQuery({
    variables: {
      slug: getSlug(),
    },
  });

  const isSuperuser = useIsSuperuser();

  const [preprocessSourceMutation] = usePreprocessSourceMutation();
  const [pendingOverlayKey, setPendingOverlayKey] = useState<string | null>(
    null
  );
  const [timeSeriesPrep, setTimeSeriesPrep] = useState<{
    current: number;
    total: number;
    error: string | null;
  } | null>(null);

  const [deleteCardMutation, deleteCardMutationState] =
    useDeleteReportCardMutation({
      onError,
      refetchQueries: refetchDraftReportTree,
      awaitRefetchQueries: true,
    });

  const handleOverlaySelection = useCallback(
    async (tocId: number, sourceId: number) => {
      await preprocessSourceMutation({
        variables: {
          slug: getSlug(),
          sourceId,
        },
        refetchQueries: [
          {
            query: ProjectReportingLayersDocument,
            variables: { slug: getSlug() },
          },
        ],
        awaitRefetchQueries: true,
      });
      // eslint-disable-next-line i18next/no-literal-string
      setPendingOverlayKey(`layer-overlay-analysis:overlay-layer-${tocId}`);
      return true;
    },
    [preprocessSourceMutation]
  );

  const [pendingLibraryLayer, setPendingLibraryLayer] = useState<{
    templateId: string;
    existing?: { stableId: string; sourceId: number };
    insert: (stableId: string) => void;
  } | null>(null);
  const [libraryLayerError, setLibraryLayerError] = useState<string | null>(
    null
  );
  const [libraryLayerBusy, setLibraryLayerBusy] = useState(false);
  const [copyLibraryTemplate] = useCopyDataLibraryTemplateMutation();

  const prepareLibraryLayer = useCallback(
    async (args: {
      templateId: string;
      existing?: { stableId: string; sourceId: number };
      insert: (stableId: string) => void;
    }) => {
      setLibraryLayerError(null);
      setLibraryLayerBusy(true);
      try {
        let stableId = args.existing?.stableId;
        let sourceId = args.existing?.sourceId;
        if (stableId == null || sourceId == null) {
          const result = await copyLibraryTemplate({
            variables: {
              slug: getSlug(),
              templateId: args.templateId,
            },
          });
          const item =
            result.data?.copyDataLibraryTemplateItem?.tableOfContentsItem;
          stableId = item?.stableId ?? undefined;
          sourceId = item?.dataLayer?.dataSourceId ?? undefined;
          if (stableId == null || sourceId == null) {
            setLibraryLayerError(tAdmin("Unable to add the layer"));
            return;
          }
          const copied = { stableId, sourceId };
          setPendingLibraryLayer((prev) =>
            prev ? { ...prev, existing: copied } : prev
          );
        }
        await preprocessSourceMutation({
          variables: {
            slug: getSlug(),
            sourceId,
          },
          refetchQueries: [
            {
              query: ProjectReportingLayersDocument,
              variables: { slug: getSlug() },
            },
          ],
          awaitRefetchQueries: true,
        });
        args.insert(stableId);
        setPendingLibraryLayer(null);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : tAdmin("Unable to add the layer");
        if (pendingLibraryLayer) {
          setLibraryLayerError(message);
        } else {
          onError(err instanceof Error ? err : new Error(message));
        }
      } finally {
        setLibraryLayerBusy(false);
      }
    },
    [
      copyLibraryTemplate,
      onError,
      pendingLibraryLayer,
      preprocessSourceMutation,
      tAdmin,
    ]
  );

  const handleEnsureDataLibraryLayer = useCallback(
    (args: {
      templateId: string;
      existing?: { stableId: string; sourceId: number };
      insert: (stableId: string) => void;
    }) => {
      if (libraryLayerBusy) return;
      setLibraryLayerError(null);
      if (args.existing) {
        void prepareLibraryLayer(args);
        return;
      }
      setPendingLibraryLayer(args);
    },
    [libraryLayerBusy, prepareLibraryLayer]
  );

  const confirmAddLibraryLayer = useCallback(async () => {
    if (!pendingLibraryLayer || libraryLayerBusy) return;
    await prepareLibraryLayer(pendingLibraryLayer);
  }, [libraryLayerBusy, pendingLibraryLayer, prepareLibraryLayer]);

  const handlePrepareTimeSeriesLayers = useCallback(
    async (args: {
      unprocessed: Array<{ title: string; sourceId: number }>;
      insert: () => void;
    }) => {
      const { unprocessed, insert } = args;
      setTimeSeriesPrep({
        current: 1,
        total: unprocessed.length,
        error: null,
      });
      try {
        for (let i = 0; i < unprocessed.length; i++) {
          const layer = unprocessed[i];
          setTimeSeriesPrep({
            current: i + 1,
            total: unprocessed.length,
            error: null,
          });
          const isLastLayer = i === unprocessed.length - 1;
          await preprocessSourceMutation({
            variables: { slug: getSlug(), sourceId: layer.sourceId },
            refetchQueries: isLastLayer
              ? [
                  {
                    query: ProjectReportingLayersDocument,
                    variables: { slug: getSlug() },
                  },
                ]
              : [],
            awaitRefetchQueries: isLastLayer,
          });
        }
        insert();
        setTimeSeriesPrep(null);
      } catch (err) {
        setTimeSeriesPrep((prev) =>
          prev
            ? {
                ...prev,
                error:
                  err instanceof Error
                    ? err.message
                    : t("An unexpected error occurred"),
              }
            : prev
        );
      }
    },
    [preprocessSourceMutation, t]
  );

  const viewRef = useRef<EditorView>();
  const root = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<EditorState>();
  const lastLangCodeRef = useRef<string>(currentLangCode);
  const lastBodyRef = useRef<any>(body);
  const initialBodyRef = useRef<any>(body);
  const saveRef = useRef<((doc: any) => void) | null>(null);
  initialBodyRef.current = body;
  const { createPortal, removePortal, setSelection, setDraftDependencies } =
    useReactNodeViewPortals();

  useEffect(() => {
    setDraftDependencies({
      metrics: hydratedDraftMetrics,
      overlaySources: draftOverlaySources,
      dependencies: draftDependencyListForHydration,
      dependencyFailuresByHash: draftDependencyFailuresByHash,
    });
  }, [
    hydratedDraftMetrics,
    draftOverlaySources,
    draftDependencyListForHydration,
    draftDependencyFailuresByHash,
    setDraftDependencies,
  ]);

  const save = useDebouncedFn(
    (doc: any) => {
      setReportBodyHasChanges(true);
      const body = doc.toJSON();
      setDraftBody(body);
      const deps = extractMetricDependenciesFromReportBody(body);
      let missing: MetricDependency[] = [];
      for (const dep of deps) {
        const hash = hashMetricDependency(dep, sourceUrlMap);
        if (metrics.find((m) => m.dependencyHash === hash)) {
          continue;
        }
        missing.push(dep);
      }
      setAdditionalDependencies(missing);
    },
    100,
    { leading: true, trailing: true },
    [
      setReportBodyHasChanges,
      setDraftBody,
      setAdditionalDependencies,
      sourceUrlMap,
    ]
  );
  saveRef.current = save;

  const contextualGroups = useMemo(
    () =>
      buildReportCommandGroups({
        projectSlug: getSlug(),
        sources:
          reportingLayersQuery.data?.projectBySlug?.reportingLayers || [],
        draftTableOfContentsItems:
          reportingLayersQuery.data?.projectBySlug?.draftTableOfContentsItems ||
          [],
        geographies: geographies,
        clippingGeography: primaryClippingGeography?.id,
        sketchClassGeometryType: sketchClass?.geometryType,
        childSketchClassGeometryTypes: Array.from(
          new Set(
            sketchClass?.project?.sketchClasses?.map((c) => c.geometryType)
          ) || []
        ),
        onProcessLayer: handleOverlaySelection,
        onPrepareTimeSeriesLayers: handlePrepareTimeSeriesLayers,
        onEnsureDataLibraryLayer: handleEnsureDataLibraryLayer,
        isSuperuser: Boolean(isSuperuser),
      }),
    [
      geographies,
      sketchClass?.geometryType,
      primaryClippingGeography?.id,
      reportingLayersQuery.data?.projectBySlug?.reportingLayers,
      reportingLayersQuery.data?.projectBySlug?.draftTableOfContentsItems,
      handleOverlaySelection,
      handlePrepareTimeSeriesLayers,
      handleEnsureDataLibraryLayer,
      sketchClass?.project?.sketchClasses,
      sketchClass?.validChildren,
      isSuperuser,
    ]
  );

  const { palette: commandPalette } = useSlashCommandPalette({
    viewRef,
    state,
    schema,
    groups: contextualGroups,
    imageUploadFile: uploadFile,
    requestedPreviewKey: pendingOverlayKey,
    onPreviewKeyApplied: () => setPendingOverlayKey(null),
  });

  useEffect(() => {
    const initialBody = initialBodyRef.current
      ? migrateInlineImagesToBlock(initialBodyRef.current)
      : undefined;
    const doc = initialBody ? Node.fromJSON(schema, initialBody) : undefined;
    const view = new EditorView(root.current!, {
      state: EditorState.create({
        schema,
        plugins,
        doc,
      }),
      handleKeyDown: (editorView, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          saveWithBody(editorView.state.doc.toJSON());
          return true;
        }
        return false;
      },
      nodeViews: {
        // @ts-ignore
        metric(node, view, getPos, decorations) {
          return createReactNodeView({
            node,
            view,
            // @ts-ignore
            getPos,
            // @ts-ignore
            decorations,
            component: ReportWidgetNodeView,
            onCreatePortal: createPortal,
            onDestroy: removePortal,
            cardId,
          });
        },
        details(node, view, getPos) {
          return new DetailsView(node, view, getPos as () => number);
        },
        tabContainer(node, view, getPos) {
          return new TabContainerView(node, view, getPos as () => number, {
            tabListLabel: t("Card tabs"),
            cardId,
          });
        },
        blockMetric(node, view, getPos, decorations) {
          return createReactNodeView({
            node,
            view,
            // @ts-ignore
            getPos,
            // @ts-ignore
            decorations,
            component: ReportWidgetNodeView,
            onCreatePortal: createPortal,
            onDestroy: removePortal,
            cardId,
          });
        },
      },
      dispatchTransaction: (transaction) => {
        const view = viewRef.current!;
        const newState = view.state.apply(transaction);
        view.updateState(newState);
        setState(newState);

        if (transaction.docChanged) {
          saveRef.current?.(newState.doc);
        }

        if (newState.selection) {
          setSelection({
            anchorPos: newState.selection.$anchor.pos,
            headPos: newState.selection.$head.pos,
          });
        } else {
          setSelection(null);
        }
      },
    });
    viewRef.current = view;
    if (preselectTitle) {
      const currentSelection = view.state.selection;
      const hasUserSelection = currentSelection && !currentSelection.empty;
      if (!hasUserSelection) {
        // update the view state to select the report title text
        let titlePos: number | null = null;
        view.state.doc.descendants((node, pos) => {
          if (node.type.name === "reportTitle") {
            titlePos = pos;
            return false; // stop descending once found
          }
          return undefined;
        });
        if (titlePos !== null) {
          const titleNode = view.state.doc.nodeAt(titlePos);
          if (titleNode) {
            const from = titlePos + 1; // position inside the node content
            const to = titlePos + titleNode.nodeSize - 1;
            const tr = view.state.tr.setSelection(
              TextSelection.create(view.state.doc, from, to)
            );
            view.dispatch(tr);
          }
        }
      }
      onEditorReadyForFocusRef.current?.(cardId, () => view.focus());
    }
    setState(view.state);
    return () => {
      view.destroy();
    };
  }, [
    isInput,
    plugins,
    schema,
    createPortal,
    removePortal,
    setSelection,
    preselectTitle,
    cardId,
    saveWithBody,
  ]);

  useEffect(() => {
    if (!pendingWidgetSettings || pendingWidgetSettings.cardId !== cardId) {
      return;
    }
    const view = viewRef.current;
    if (!view) {
      return;
    }
    const { widgetPosition } = pendingWidgetSettings;
    if (widgetPosition < 0 || widgetPosition > view.state.doc.content.size) {
      clearPendingWidgetSettings();
      return;
    }
    const node = view.state.doc.nodeAt(widgetPosition);
    if (
      !node ||
      (node.type.name !== "metric" && node.type.name !== "blockMetric")
    ) {
      clearPendingWidgetSettings();
      return;
    }
    const tr = view.state.tr.setSelection(
      NodeSelection.create(view.state.doc, widgetPosition)
    );
    view.dispatch(tr);
    clearPendingWidgetSettings();
    setTimeout(() => {
      view.dom.focus({ preventScroll: true });
    }, 30);
  }, [cardId, clearPendingWidgetSettings, pendingWidgetSettings, viewRef]);

  // Update editor state when language changes (body will be different for different languages)
  useEffect(() => {
    if (viewRef.current && body) {
      const langChanged = lastLangCodeRef.current !== currentLangCode;

      // Always update when language changes to show the correct language's content
      if (langChanged) {
        const doc = Node.fromJSON(schema, migrateInlineImagesToBlock(body));
        const newState = EditorState.create({
          schema,
          plugins,
          doc,
        });
        viewRef.current.updateState(newState);
        setState(newState);

        lastLangCodeRef.current = currentLangCode;
        lastBodyRef.current = body;
      }
    }
  }, [body, schema, plugins, currentLangCode]);

  // Keep refs up to date with latest props
  useEffect(() => {
    lastLangCodeRef.current = currentLangCode;
    lastBodyRef.current = body;
  }, [body, currentLangCode]);

  return (
    <div className={`relative ${className}`}>
      <style>
        {
          // eslint-disable-next-line i18next/no-literal-string
          BLUR_SELECTION_STYLES
        }
      </style>
      <TooltipMenu
        view={viewRef.current}
        state={state}
        schema={schema}
        imageUploadFile={uploadFile}
      />
      {commandPalette}
      {pendingLibraryLayer && (
        <Modal
          title={tAdmin("Add World Ports")}
          onRequestClose={() => {
            if (libraryLayerBusy) return;
            setPendingLibraryLayer(null);
          }}
          footer={[
            {
              label: tAdmin("Cancel"),
              variant: "secondary",
              disabled: libraryLayerBusy,
              onClick: () => setPendingLibraryLayer(null),
            },
            {
              label: tAdmin("Add layer"),
              variant: "primary",
              loading: libraryLayerBusy,
              onClick: () => {
                void confirmAddLibraryLayer();
              },
            },
          ]}
        >
          <p>
            {tAdmin(
              "Distance to Port uses the World Ports layer from the data library. Add it to this project?"
            )}
          </p>
          {libraryLayerError && (
            <p className="mt-2 text-sm text-red-600">{libraryLayerError}</p>
          )}
        </Modal>
      )}
      {timeSeriesPrep && (
        <TimeSeriesProcessingModal
          open
          current={timeSeriesPrep.current}
          total={timeSeriesPrep.total}
          error={timeSeriesPrep.error}
          onDismiss={() => setTimeSeriesPrep(null)}
        />
      )}
      <div
        className={`ProseMirrorBody ReportCardBodyEditor ReportCardBody`}
        ref={root}
      ></div>
      {footerContainerRef.current &&
        createReactPortal(
          <>
            {showCalcDetails && cardId === showCalcDetails && (
              <CalculationDetailsModal
                state={{ open: true, cardId }}
                onClose={() => setShowCalcDetails(undefined)}
                metrics={allDependencies.metrics}
                config={report.tabs
                  ?.find((t) => t.cards?.find((c) => c.id === cardId))
                  ?.cards?.find((c) => c.id === cardId)}
                adminMode={true}
              />
            )}

            <div
              className="p-2 text-sm bg-gray-50 border-t border-gray-200 shadow-inner rounded-b-lg"
              data-report-card-body-editor-footer="true"
            >
              <div className="flex items-center space-x-2 justify-end">
                <div className="pr-5">
                  <button
                    onClick={() => {
                      setShowCalcDetails(cardId);
                    }}
                  >
                    <ReportCardLoadingIndicator
                      display={true}
                      metrics={allDependencies.metrics}
                      sourceProcessingJobs={
                        allDependencies.sourceProcessingJobs
                      }
                      showWhenComplete={true}
                    />
                  </button>
                </div>
                <Button
                  small
                  label={t("Cancel")}
                  disabled={deleteCardMutationState.loading}
                  loading={deleteCardMutationState.loading}
                  onClick={() => {
                    if (preselectTitle) {
                      deleteCardMutation({
                        variables: {
                          id: cardId,
                        },
                      }).then(() => {
                        setEditing(null);
                      });
                    } else {
                      setEditing(null);
                    }
                  }}
                />
                <Button
                  small
                  label={t("Save")}
                  onClick={() => handleCardSave()}
                  disabled={
                    updateReportCardState.loading ||
                    deleteCardMutationState.loading
                  }
                  loading={updateReportCardState.loading}
                  primary
                />
              </div>
            </div>
          </>,
          footerContainerRef.current
        )}
    </div>
  );
}

function OverlayPickerContent({
  onSelect,
  isLoading,
  loadingId,
  errorMessage,
}: {
  onSelect: (overlayId: number) => Promise<boolean>;
  isLoading: boolean;
  loadingId: number | null;
  errorMessage?: string | null;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <LayerPickerList
      className="w-72"
      hideSearch={false}
      required
      onlyReportingLayers={false}
      // optionsOverride={overlays.map((option) => ({
      //   stableId: option.stableId || String(option.id),
      //   title: option.title,
      //   tableOfContentsItemId: option.id,
      //   reporting: true,
      // }))}
      onSelect={async (val) => {
        if (!val?.tableOfContentsItemId) return;
        if (isLoading) return;
        const success = await onSelect(val.tableOfContentsItemId);
        if (!success && inputRef.current) {
          inputRef.current.focus();
        }
      }}
    />
  );
}

export default function ReportCardBodyEditor(props: ReportCardBodyEditorProps) {
  return <ReportCardBodyEditorInner {...props} />;
}
